import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import HelpGuide from './HelpGuide';
import { reviewRequestsApi, notificationsApi, settingsApi } from '../services/api';
import { 
  LayoutDashboard, 
  FileSpreadsheet, 
  TrendingUp, 
  Sparkles, 
  Settings, 
  Upload,
  LogOut,
  Menu,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  Pin,
  PinOff,
  ChevronDown,
  ArrowUpDown,
  FileText,
  Calculator,
  ClipboardCheck,
  GitBranch,
  CheckSquare,
  Target,
  Bell,
  Banknote
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { branding, isDark, toggleTheme, getCurrentLogo, setSelectedBu, selectedBuId } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [sidebarPinned, setSidebarPinned] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [userBuLogo, setUserBuLogo] = useState(null);

  // Function to load notification counts - extracted so it can be called on demand
  const loadCounts = async () => {
    try {
      // Only load approval count if user is CFO or admin
      if (user?.is_cfo || user?.role === 'admin') {
        const approvalResponse = await reviewRequestsApi.getPendingCount();
        setPendingApprovalsCount(approvalResponse.data.count || 0);
      } else {
        setPendingApprovalsCount(0);
      }
      
      const notificationResponse = await notificationsApi.getUnreadCount();
      setUnreadNotificationsCount(notificationResponse.data.count || 0);
    } catch (error) {
      console.error('Error loading counts:', error);
    }
  };

  // Load notification counts on mount and set up interval
  useEffect(() => {
    if (user) {
      loadCounts();
      // Refresh counts every 30 seconds
      const interval = setInterval(loadCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Load business units and set user's BU logo
  useEffect(() => {
    const loadBuData = async () => {
      try {
        const res = await settingsApi.getBusinessUnits();
        setBusinessUnits(res.data);
        
        // If user is not admin and has a business_unit_id, set their BU logo
        if (!isAdmin() && user?.business_unit_id) {
          const userBu = res.data.find(bu => bu.id === user.business_unit_id);
          if (userBu?.logo_url) {
            setUserBuLogo(userBu.logo_url);
            setSelectedBu(user.business_unit_id, userBu.logo_url);
          }
        }
      } catch (error) {
        console.error('Failed to load business units:', error);
      }
    };
    
    if (user) {
      loadBuData();
    }
  }, [user, isAdmin, setSelectedBu]);

  // Listen for custom event to refresh counts (triggered by Approvals page, etc.)
  useEffect(() => {
    const handleRefreshCounts = () => {
      loadCounts();
    };
    
    window.addEventListener('refreshNotificationCounts', handleRefreshCounts);
    return () => {
      window.removeEventListener('refreshNotificationCounts', handleRefreshCounts);
    };
  }, [user]);

  // Build nav items based on user role
  // All users see: Dashboard, Budget vs Actuals, Budgets, Financial Review, Reports
  const baseNavItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/budget', label: 'Budget vs Actuals', icon: TrendingUp },
    { path: '/budgets', label: 'Budgets', icon: Calculator },
    { path: '/financial-review', label: 'Financial Review', icon: ClipboardCheck },
    { path: '/reports', label: 'Reports', icon: FileText },
  ];
  
  // Admin-only items (alphabetically ordered)
  const adminNavItems = [
    { path: '/insights', label: 'AI Insights', icon: Sparkles },
    { path: '/approvals', label: 'Approvals', icon: CheckSquare, badge: pendingApprovalsCount },
    { path: '/cashflow', label: 'Cashflow', icon: Banknote },
    { path: '/forecasts', label: 'Forecasts', icon: Target },
    { path: '/import-export', label: 'Import & Export', icon: ArrowUpDown },
    { path: '/settings', label: 'Settings', icon: Settings },
    { path: '/what-if', label: 'What-If Scenarios', icon: GitBranch },
  ];
  
  // Combine based on role
  const navItems = isAdmin() ? [...baseNavItems, ...adminNavItems] : baseNavItems;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const showExpanded = sidebarPinned ? sidebarExpanded : (sidebarExpanded || isHovering);

  return (
    <TooltipProvider>
      <div className={`min-h-screen flex ${isDark ? 'dark bg-zinc-950' : 'bg-slate-50'}`} data-testid="main-layout">
        {/* Sidebar */}
        <aside 
          className={`fixed left-0 top-0 h-full z-40 transition-all duration-300 ${
            showExpanded ? 'w-64' : 'w-16'
          } ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-slate-200'} border-r flex flex-col`}
          onMouseEnter={() => !sidebarPinned && setIsHovering(true)}
          onMouseLeave={() => !sidebarPinned && setIsHovering(false)}
          data-testid="sidebar"
        >
          {/* Logo - same height as header */}
          <div className={`h-16 flex items-center justify-center border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'} ${showExpanded ? 'px-3' : 'px-1'}`}>
            <Link to="/" className="flex items-center justify-center">
              <div className={`flex items-center justify-center bg-white rounded-lg shadow-sm transition-all duration-200 ${showExpanded ? 'h-12 px-4' : 'h-11 w-11'}`}>
                <img 
                  src={getCurrentLogo()} 
                  alt={branding?.company_name || 'FP&A'}
                  className={`object-contain ${showExpanded ? 'h-10 max-w-[180px]' : 'h-9 w-9'}`}
                  data-testid="sidebar-logo"
                />
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-2 overflow-y-auto">
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Tooltip key={item.path} delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors relative ${
                          isActive 
                            ? isDark 
                              ? 'bg-zinc-800 text-white' 
                              : 'bg-slate-100 text-slate-900'
                            : isDark
                              ? 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                        {showExpanded && (
                          <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                        )}
                        {item.badge > 0 && (
                          <span className={`absolute ${showExpanded ? 'right-2' : 'top-0 right-0'} min-w-[18px] h-[18px] px-1 text-xs font-bold bg-red-500 text-white rounded-full flex items-center justify-center`}>
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </Link>
                    </TooltipTrigger>
                    {!showExpanded && (
                      <TooltipContent side="right">
                        {item.label}
                        {item.badge > 0 && ` (${item.badge})`}
                      </TooltipContent>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          </nav>

          {/* Sidebar Controls */}
          <div className={`p-2 border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
            <div className="flex items-center justify-between">
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSidebarExpanded(!sidebarExpanded)}
                    className={`rounded-sm ${isDark ? 'text-zinc-400 hover:text-white hover:bg-zinc-800' : ''}`}
                    data-testid="toggle-sidebar"
                  >
                    {sidebarExpanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {sidebarExpanded ? 'Collapse' : 'Expand'}
                </TooltipContent>
              </Tooltip>
              
              {showExpanded && (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSidebarPinned(!sidebarPinned)}
                      className={`rounded-sm ${isDark ? 'text-zinc-400 hover:text-white hover:bg-zinc-800' : ''}`}
                      data-testid="pin-sidebar"
                    >
                      {sidebarPinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar'}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className={`flex-1 flex flex-col transition-all duration-300 ${showExpanded ? 'ml-64' : 'ml-16'}`}>
          {/* Top Header */}
          <header className={`sticky top-0 z-30 h-16 border-b ${
            isDark 
              ? 'bg-zinc-900/80 border-zinc-800 backdrop-blur-md' 
              : 'bg-white/80 border-slate-200 backdrop-blur-md'
          }`}>
            <div className="flex items-center justify-between px-6 h-full">
              {/* Left side - page title */}
              <div className="flex items-center gap-3">
                <h2 className={`font-display text-lg font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
                </h2>
              </div>

              {/* Right side - actions */}
              <div className="flex items-center gap-3">
                {/* Help Guide */}
                <HelpGuide />

                {/* Theme Toggle */}
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleTheme}
                      className={`rounded-sm ${isDark ? 'text-zinc-400 hover:text-white hover:bg-zinc-800' : ''}`}
                      data-testid="theme-toggle"
                    >
                      {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isDark ? 'Light mode' : 'Dark mode'}
                  </TooltipContent>
                </Tooltip>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className={`flex items-center gap-2 p-2 rounded-sm transition-colors ${
                        isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-100'
                      }`}
                      data-testid="user-menu-btn"
                    >
                      {user?.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          alt={user?.name || 'User'} 
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-emerald-700">
                            {user?.name?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                      )}
                      <div className="hidden md:block text-left">
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}>
                          {user?.name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                          {user?.role === 'admin' ? 'Administrator' : 'User'}
                        </p>
                      </div>
                      <ChevronDown className={`w-4 h-4 hidden md:block ${isDark ? 'text-zinc-400' : 'text-slate-400'}`} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 flex items-center gap-3">
                      {user?.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          alt={user?.name || 'User'} 
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-medium text-emerald-700">
                            {user?.name?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium">{user?.name}</p>
                        <p className="text-xs text-slate-500">{user?.email}</p>
                      </div>
                    </div>
                    <div className="px-3 pb-2">
                      <div className="flex gap-1">
                        {user?.permissions?.map((perm, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    {isAdmin() && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/settings')}>
                          <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className={`flex-1 ${isDark ? 'bg-zinc-950' : 'bg-slate-50'}`}>
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Layout;
